import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Spinner } from '@/shared/ui/spinner';
import { z } from 'zod';
import { fetchStoreUser, saveStoreUser, type StoreUser } from '../api/use-store-users';

/** `data-minlength="6"` no formulário do legado. */
const MIN_PASSWORD = 6;

const createSchema = z.object({
  name: z.string().trim().min(1, 'Digite o nome'),
  email: z.string().trim().min(1, 'Digite o email').email('O email é inválido'),
  password: z.string().min(MIN_PASSWORD, `A senha precisa de ao menos ${MIN_PASSWORD} caracteres`),
  position: z.string().trim().min(1, 'Digite a loja'),
});

/** Na edição a senha é opcional: em branco significa "manter a atual". */
const updateSchema = createSchema.extend({
  password: z
    .string()
    .refine((v) => v === '' || v.length >= MIN_PASSWORD, {
      message: `A senha precisa de ao menos ${MIN_PASSWORD} caracteres`,
    }),
});

type FormValues = z.infer<typeof createSchema>;

const EMPTY: FormValues = { name: '', email: '', password: '', position: '' };

interface Props {
  idPassbook: string;
  /** `null` abre em modo criação. */
  user: StoreUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function StoreUserFormDialog({ idPassbook, user, open, onOpenChange, onSaved }: Props) {
  const editing = Boolean(user);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  /** Hash atual, reenviado quando o campo de senha fica em branco na edição. */
  const [currentHash, setCurrentHash] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(editing ? updateSchema : createSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    setShowPassword(false);

    if (!user) {
      setCurrentHash(null);
      form.reset(EMPTY);
      return;
    }

    // Modo edição: o legado busca o usuário em `GET /user/:id` para montar o form.
    form.reset({
      name: user.profile?.name ?? '',
      email: user.email ?? '',
      password: '',
      position: user.profile?.position ?? '',
    });
    setCurrentHash(user.password ?? null);
    setLoading(true);
    fetchStoreUser(idPassbook, user._id)
      .then((full) => {
        setCurrentHash(full.password ?? null);
        form.reset({
          name: full.profile?.name ?? '',
          email: full.email ?? '',
          password: '',
          position: full.profile?.position ?? '',
        });
      })
      .catch(() => {
        // Segue com os dados da listagem; a senha só será exigida se não houver hash.
      })
      .finally(() => setLoading(false));
  }, [open, user, idPassbook, form]);

  const submit = form.handleSubmit(async (values) => {
    // Em branco na edição = manter a senha: reenviamos o hash, que o Mongoose
    // enxerga como "não modificado". Sem hash conhecido, exigimos uma nova senha.
    let password = values.password;
    if (editing && password === '') {
      if (!currentHash) {
        form.setError('password', { message: 'Digite uma nova senha para este usuário' });
        return;
      }
      password = currentHash;
    }

    try {
      await saveStoreUser({
        idPassbook,
        id: user?._id,
        name: values.name.trim(),
        email: values.email.trim(),
        password,
        position: values.position.trim(),
      });
      toast.success(editing ? 'Usuário atualizado.' : 'Usuário cadastrado.');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao salvar o usuário.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar usuário' : 'Cadastrar usuário'}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4">
            <Field label="Nome" error={form.formState.errors.name?.message}>
              <Input placeholder="Digite o nome" {...form.register('name')} />
            </Field>

            <Field label="Email" error={form.formState.errors.email?.message}>
              {/* O legado bloqueia o email na edição: é a chave de busca do registro. */}
              <Input
                type="email"
                placeholder="Digite o email"
                readOnly={editing}
                className={editing ? 'bg-muted' : undefined}
                {...form.register('email')}
              />
            </Field>

            <Field
              label="Senha"
              error={form.formState.errors.password?.message}
              hint={editing ? 'Deixe em branco para manter a senha atual.' : undefined}
            >
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={editing ? 'Manter senha atual' : 'Digite a senha'}
                  className="pr-10"
                  {...form.register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </Field>

            <Field label="Loja" error={form.formState.errors.position?.message}>
              <Input placeholder="Digite a loja" {...form.register('position')} />
            </Field>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {hint && !error ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
